import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { getOlaTokens, type AssetResponse } from '../../api';
import { signedUrlStaleTime } from './useAssetUrls';

/**
 * Capability tokens for the OLA banners currently on screen. Why a token rather
 * than a signed URL: see the header of `apps/asset-library/backend/src/api/ola_serve.py`.
 *
 * Unlike signing, minting a token is pure CPU: no IAM round trip, so asking for
 * a whole page at once is cheap and there is no error-driven `refresh` here.
 * Expiry is still taken from the response rather than assumed, reusing
 * `signedUrlStaleTime` so both kinds of short-lived credential are refreshed on
 * the same headroom.
 */

export interface OlaTokenLookup {
  /** assetId -> token. Absent means "no token", not "still loading". */
  tokenById: Map<string, string>;
}

/** The assets on this page that are banners, in a form the hook can key on. */
export function olaAssetIds(assets: AssetResponse[]): string[] {
  return assets.filter((asset) => asset.asset_type === 'ola').map((asset) => asset.id);
}

export function useOlaTokens(assetIds: string[]): OlaTokenLookup {
  // Sorted so the cache key is order-independent: re-sorting the grid must not
  // look like a different set of assets and mint a fresh round of tokens.
  const key = useMemo(() => [...assetIds].sort(), [assetIds]);

  const { data } = useQuery({
    queryKey: ['asset-library', 'ola-tokens', key],
    queryFn: () => getOlaTokens(key),
    enabled: key.length > 0,
    staleTime: (query) => signedUrlStaleTime(query.state.data?.expires_in_seconds),
    refetchInterval: (query) => signedUrlStaleTime(query.state.data?.expires_in_seconds) || false,
  });

  return useMemo(
    () => ({
      tokenById: new Map((data?.tokens ?? []).map(({ asset_id, token }) => [asset_id, token])),
    }),
    [data],
  );
}
